import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { Circle, ListItem, useBoxStyle, AHSeparator } from "arkh";
import { FlatList } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useMemo } from "react";
import { WidgetCalendarOverviewSettingsQuery } from "./__generated__/WidgetCalendarOverviewSettingsQuery.graphql";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { UserLoggedInNavigatorNavigationProp } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useWACalendarOverviewContext } from "./WACalendarOverviewContext";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

type ParamList = {
  WACalendarOverviewSelectCalendars: object;
};

type WidgetCalendarOverviewSettingsScreenProp<T extends keyof ParamList> = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, T>,
    UserLoggedInNavigatorNavigationProp<"WidgetAddCalendarOverview">
  >;
  route: RouteProp<ParamList, T>;
};

const Stack = createNativeStackNavigator<ParamList>();

function InnerWidgetCalendarOverviewSelectCalendars(
  props: WidgetCalendarOverviewSettingsScreenProp<"WACalendarOverviewSelectCalendars"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WidgetCalendarOverviewSettingsQuery>(
    graphql`
      query WidgetCalendarOverviewSettingsQuery {
        store {
          calendars {
            id
            title
            color
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const { calendarIds, setCalendarIds } = useWACalendarOverviewContext();
  const listData = useMemo(() => {
    return data.store.calendars;
  }, [data]);

  const boxStyle = useBoxStyle({
    space: "medium",
  });

  return (
    <FlatList
      contentContainerStyle={boxStyle}
      data={listData}
      renderItem={({ item, index }) => (
        <ListItem
          border="border-primary"
          isFirst={index === 0}
          isLast={index === listData.length - 1}
          left={<Circle size="small" style={{ backgroundColor: item.color }} />}
          right={
            <ListItem.MultiSelectIndicator
              selected={
                calendarIds.length === 0 || calendarIds.includes(item.id)
              }
            />
          }
          onPress={() => {
            if (calendarIds.length === 0) {
              setCalendarIds(
                data.store.calendars.flatMap((calendar) =>
                  calendar.id === item.id ? [] : [calendar.id]
                )
              );
            } else if (calendarIds.includes(item.id)) {
              setCalendarIds(calendarIds.filter((id) => id !== item.id));
            } else {
              setCalendarIds([...calendarIds, item.id]);
            }
          }}
        >
          <ListItem.Title>{item.title}</ListItem.Title>
        </ListItem>
      )}
      ItemSeparatorComponent={AHSeparator}
      keyExtractor={(item) => item.id}
    />
  );
}

function WidgetCalendarOverviewSelectCalendars(
  props: WidgetCalendarOverviewSettingsScreenProp<"WACalendarOverviewSelectCalendars">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWidgetCalendarOverviewSelectCalendars
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export function WidgetCalendarOverviewSettings(props: { onClose(): void }) {
  const getHeaderOptions = useNavigationHeaderOptions();
  const intl_tokens = useIntlTokens();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="WACalendarOverviewSelectCalendars"
        component={WidgetCalendarOverviewSelectCalendars}
        options={getHeaderOptions({
          type: "CANCEL",
          onClose: props.onClose,
          title: intl_tokens.SelectCalendars,
          defaultContentStyle: true,
        })}
      />
    </Stack.Navigator>
  );
}
